import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, FormControl, InputLabel, Select, MenuItem, Alert, Button, Typography, Snackbar, CircularProgress } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { getSprintHealthTeams, getSprintHealthSprints, shortSprint, refreshTeamSprint } from '../../../services/sprintHealthService';
import { isAuthenticated } from '../../../services/authService';
import sprintSyncStatusService from '../../../services/sprintSyncStatusService';
import { getDefaultSprintByCurrentDate } from '../../../services/sprintMetricsService';
import TeamHealthView from './TeamHealthView';
import LeadershipHealthView from './LeadershipHealthView';
import EmailPreviewDialog from './EmailPreviewDialog';
import { SegToggle } from './shared';

// Team view first, as in the approved layout; the tab still opens on the leadership view.
const VIEWS = [['team', 'Team view'], ['leadership', 'Leadership view']];
const md = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');

/**
 * Sprint Review when "New Sprint KPI" is on. Opens on the leadership view (all teams);
 * Team view shows one team's sprint. Deep links: ?view=team&team=<id>&sprint=<name>.
 */
const SprintHealthTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [teams, setTeams] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshProblems, setRefreshProblems] = useState(null);
  const [toast, setToast] = useState('');

  const view = searchParams.get('view') === 'team' ? 'team' : 'leadership';
  const team = searchParams.get('team') || teams[0]?.id || '';
  const sprint = searchParams.get('sprint') || '';

  const setParams = (patch) => setSearchParams((prev) => {
    const n = new URLSearchParams(prev);
    Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)));
    return n;
  }, { replace: true });

  useEffect(() => {
    (async () => {
      try {
        const [t, s] = await Promise.all([getSprintHealthTeams(), getSprintHealthSprints()]);
        setTeams(t.data || []);
        setSprints(s.data || []);
        if (!searchParams.get('sprint')) {
          const name = getDefaultSprintByCurrentDate(s.data || []);
          if (name) setParams({ sprint: name });
        }
      } catch (err) {
        setError(err.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const teamName = teams.find((t) => t.id === team)?.displayName || team;
  const knownSprint = sprints.some((s) => s.sprintName === sprint);
  const sprintDoc = sprints.find((s) => s.sprintName === sprint);
  // Refresh pulls this team and sprint from ADO (work items, sprint data, goal, commitments), then the view reloads.
  const refresh = async () => {
    setRefreshing(true);
    setRefreshProblems(null);
    sprintSyncStatusService.start(`Refreshing ${teamName} · ${shortSprint(sprint)} from ADO…`);
    try {
      const res = await refreshTeamSprint(sprint, team);
      const failed = (res.data?.steps || []).filter((s) => !s.ok);
      if (failed.length) setRefreshProblems(failed);
      else setToast(`${teamName} · ${shortSprint(sprint)} refreshed from ADO`);
    } catch (err) {
      setRefreshProblems([{ label: 'Refresh', detail: err.message }]);
    } finally {
      sprintSyncStatusService.stop();
      setRefreshing(false);
      setRefreshKey((k) => k + 1);
    }
  };

  const toggle = <SegToggle value={view} options={VIEWS} onChange={(v) => setParams({ view: v === 'leadership' ? null : v })} ariaLabel="Audience" />;

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px 24px', justifyContent: 'space-between', alignItems: 'flex-end', pb: 2 }}>
        <Box>
          <Typography component="h1" sx={{ fontSize: 26, fontWeight: 600, letterSpacing: '-.01em', color: '#16231d' }}>Sprint review</Typography>
          <Typography sx={{ color: '#4b5a52', mt: 0.25 }}>
            {[view === 'team' ? teamName : 'All teams', shortSprint(sprint), sprintDoc ? `${md(sprintDoc.startDate)} – ${md(sprintDoc.endDate)}` : null].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}>
          {view === 'team' && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="sh-team-label">Team</InputLabel>
              <Select labelId="sh-team-label" id="sh-team" label="Team" value={teams.some((t) => t.id === team) ? team : ''} onChange={(e) => setParams({ team: e.target.value })}>
                {teams.map((t) => <MenuItem key={t.id} value={t.id}>{t.displayName}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="sh-sprint-label">Sprint</InputLabel>
            <Select labelId="sh-sprint-label" id="sh-sprint" label="Sprint" value={knownSprint ? sprint : ''} onChange={(e) => setParams({ sprint: e.target.value })}>
              {sprints.map((s) => <MenuItem key={s.sprintName} value={s.sprintName}>{s.sprintName}</MenuItem>)}
            </Select>
          </FormControl>
          {view === 'team' && isAuthenticated() && (
            <Button
              variant="outlined"
              onClick={refresh}
              disabled={!sprint || !team || refreshing}
              startIcon={refreshing ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <RefreshIcon />}
              sx={{ color: '#16231d', borderColor: '#c9d1cb', '&:hover': { borderColor: '#16231d', bgcolor: 'rgba(22,35,29,0.04)' } }}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
          )}
          <Button variant="contained" onClick={() => setPreview(true)} disabled={!sprint || (view === 'team' && !team)} sx={{ bgcolor: '#16231d', '&:hover': { bgcolor: '#2b3a32' } }}>
            {view === 'team' ? 'Preview leadership email' : 'Preview email'}
          </Button>
        </Box>
      </Box>

      {refreshProblems && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setRefreshProblems(null)}>
          Refreshed, but some parts could not be updated:
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {refreshProblems.map((s) => <li key={s.label}><b>{s.label}</b>: {s.detail}</li>)}
          </Box>
        </Alert>
      )}
      {view === 'leadership' && sprint && (
        <LeadershipHealthView sprint={sprint} onOpenTeam={(t) => setParams({ view: 'team', team: t })} toggle={toggle} />
      )}
      {view === 'team' && team && sprint && (
        <TeamHealthView sprint={sprint} team={team} toggle={toggle} refreshKey={refreshKey} />
      )}

      <EmailPreviewDialog
        open={preview}
        onClose={() => setPreview(false)}
        sprint={sprint}
        team={view === 'team' ? team : null}
        teamName={teamName}
      />
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast('')} message={toast} />
    </Box>
  );
};

export default SprintHealthTab;
